/* @flow */
import * as React from 'react';

import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator.jsx';
import PeopleListGroupProfileDataContainer from './PeopleListGroupProfileDataContainer.jsx';
import PeopleListUserProfileDataContainer from './PeopleListUserProfile';
import PeopleListBatchActions from './PeopleListBatchActions';
import type { Props as BatchProps } from './PeopleListBatchActions';

export type PeopleListSidebarType = 'batch' | 'group-profile' | 'user-profile' | 'workflows';

type WorkflowSidebarData = {
  type: 'workflows',
};

type GroupProfileSidebarData = {
  type: 'group-profile',
  data: {
    entityUuid: string,
  },
};

type UserProfileSidebarData = {
  type: 'user-profile',
  data: {
    userId: number,
    organizationUuid: string,
  },
};

type BatchSidebarData = {
  type: 'batch',
  data: BatchProps,
};

export type SidebarData =
  | WorkflowSidebarData
  | GroupProfileSidebarData
  | UserProfileSidebarData
  | BatchSidebarData;

type Props = {
  sidebarProps: SidebarData,
  onClose: () => void,
};

type State = {
  currentSidebarData: ?SidebarData,
  history: SidebarData[],
};

export default class PeopleListSidebar extends React.Component<Props, State> {
  state: State = {
    currentSidebarData: null,
    history: [],
  };

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    if (prevState.currentSidebarData !== nextProps.sidebarProps) {
      return { history: [], currentSidebarData: nextProps.sidebarProps };
    }

    return null;
  }

  handleEntityClicked = (newSidebarData: SidebarData) => {
    const { history, currentSidebarData } = this.state;
    const newHistory = history.slice();

    if (currentSidebarData) {
      newHistory.push(currentSidebarData);
    }

    this.setState({ history: newHistory, currentSidebarData: newSidebarData });
  };

  handleGroupClicked = (entityUuid: string) => {
    this.handleEntityClicked({ type: 'group-profile', data: { entityUuid } });
  };

  handleUserClicked = (userId: number, organizationUuid: string) => {
    this.handleEntityClicked({ type: 'user-profile', data: { userId, organizationUuid } });
  };

  handleFamilyMemberClicked = (sidebarData: UserProfileSidebarData) => {
    const { organizationUuid } = sidebarData.data;

    return (userId: number) => {
      this.handleEntityClicked({ type: 'user-profile', data: { userId, organizationUuid } });
    };
  };

  handleBackClicked = () => {
    const newHistory = this.state.history.slice();
    const sidebarData = newHistory.pop();
    this.setState({ history: newHistory, currentSidebarData: sidebarData });
  };

  renderUserProfilePane = (sidebarData: UserProfileSidebarData) => {
    const { data } = sidebarData;
    const { history } = this.state;
    const { onClose } = this.props;
    const backCallback = history.length > 0 ? this.handleBackClicked : null;

    return (
      <PeopleListUserProfileDataContainer
        userId={data.userId}
        organizationUuid={data.organizationUuid}
        data-test="user-profile"
        onBackClicked={backCallback}
        onGroupClicked={this.handleGroupClicked}
        onFamilyMemberClicked={this.handleFamilyMemberClicked(sidebarData)}
        onClose={onClose}
      />
    );
  };

  renderGroupProfilePane = (sidebarData: GroupProfileSidebarData) => {
    const { data } = sidebarData;
    const { onClose } = this.props;
    const { history } = this.state;
    const backCallback = history.length > 0 ? this.handleBackClicked : null;

    return (
      <PeopleListGroupProfileDataContainer
        groupUuid={data.entityUuid}
        data-test="group-profile"
        onBackClicked={backCallback}
        onEntityClicked={this.handleEntityClicked}
        onUserClicked={this.handleUserClicked}
        onClose={onClose}
      />
    );
  };

  renderBatchProfilePane = (sidebarData: BatchSidebarData) => {
    return <PeopleListBatchActions data-test="batch" {...sidebarData.data} />;
  };

  render() {
    const { currentSidebarData } = this.state;

    if (!currentSidebarData) {
      return <CenteredLoadingIndicator height={50} size="small" />;
    }

    return (
      <aside>
        {currentSidebarData.type === 'workflows' && <span data-test="workflows">Workflows</span>}
        {currentSidebarData.type === 'batch' && this.renderBatchProfilePane(currentSidebarData)}
        {currentSidebarData.type === 'user-profile' &&
          this.renderUserProfilePane(currentSidebarData)}
        {currentSidebarData.type === 'group-profile' &&
          this.renderGroupProfilePane(currentSidebarData)}
      </aside>
    );
  }
}
